<script setup lang="ts" generic="T">
import { computed, ref } from 'vue';
import { useDropdownPosition } from 'src/composables/useDropdownPosition';
import 'src/assets/ticket-edit-dropdown.css';

const props = withDefaults(defineProps<{
  active?: string | null;
  name?: string;
  open?: boolean;
  items?: T[];
  activeId?: string | number | null;
  idKey?: string;
  labelKey?: string;
  maxHeight?: string;
  minWidth?: string;
  showClear?: boolean;
  clearLabel?: string;
}>(), {
  idKey: 'id',
  labelKey: 'name',
  clearLabel: 'Clear',
});

const emit = defineEmits<{
  select: [item: T];
  clear: [];
}>();

const isVisible = computed(() => {
  if (props.name != null) return props.active === props.name;
  return props.open ?? false;
});

function getId(item: T): unknown {
  return (item as any)?.[props.idKey];
}

function getLabel(item: T): string {
  return (item as any)?.[props.labelKey] ?? '';
}

function isActive(item: T): boolean {
  return props.activeId != null && getId(item) === props.activeId;
}

const dropdownEl = ref<HTMLElement>();
const { flipAbove } = useDropdownPosition(dropdownEl);
</script>

<template>
  <div
    v-if="isVisible"
    ref="dropdownEl"
    class="te-dropdown"
    :class="{ 'te-dropdown-above': flipAbove }"
    :style="{
      maxHeight: maxHeight || undefined,
      minWidth: minWidth || undefined,
    }"
  >
    <template v-if="items">
      <button
        v-for="item in items"
        :key="String(getId(item))"
        class="te-dropdown-item"
        :class="{ 'te-dropdown-item-active': isActive(item) }"
        @click.stop="emit('select', item)"
      >
        <slot name="item" :item="item" :active="isActive(item)" :label="getLabel(item)">
          {{ getLabel(item) }}
        </slot>
      </button>
    </template>
    <slot v-else />
    <div v-if="showClear" class="border-t border-surface-200 dark:border-surface-700 px-2 py-1.5">
      <button class="te-dropdown-item w-full text-xs text-red-500" @click.stop="emit('clear')">
        <i class="pi pi-times" style="font-size: 10px" />
        <span class="ml-2">{{ clearLabel }} </span>
      </button>
    </div>
  </div>
</template>
